/*
 * ai-carousel.js — Subsystem 3 / Phase 1 personalized homepage carousel controller.
 *
 * Loads EXACTLY like the native carousels (d_featured-collection): the section renders REAL
 * fallback-collection products + .tbb-native-loading-overlay, and the CSS rule
 * `.tbb-carousel-shell:has(img[loaded]) .tbb-native-loading-overlay` shows the vinyl loader
 * until the first real image paints. No placeholder cards, no JS-managed loader.
 *
 * This controller only layers personalization on top of that native behavior:
 *   - 50/50 sticky session A/B bucket (tbb_ai_carousel_bucket cookie) + GTM dataLayer push
 *   - exposes window.tbb_engine.{ klaviyo_id, client_id } (opaque ids only; never email/phone)
 *       client_id  = Shopify first-party _shopify_y (same source the Web Pixel uses)
 *       klaviyo_id = Klaviyo profile_id for identified shoppers; null for anonymous
 *   - treatment bucket + engine flag ON: fetch ranked products from /v1/carousel, then a single
 *       Section Rendering call (?section_id=ai-carousel&q=id:... OR ...) and swap [data-ai-content].
 *       Swapping removes the loaded fallback images, so the native :has(img[loaded]) overlay
 *       re-covers the row until the first engine image paints — same loader as first paint.
 *   - control / engine OFF / timeout / error / empty -> the fallback products just stay, exactly
 *       like any other carousel. Nothing is hidden, nothing flashes a placeholder.
 *
 * Guardrails: no per-request LLM; the only cookie set is the A/B bucket; identity = Klaviyo + clientId.
 * The engine fetch/swap is gated behind the section's `engine_enabled` setting.
 */
(async () => {
    const { Core } = await importModule('Core');
    const { fetchHTML } = await importModule('Utils');

    const ENGINE_ENDPOINT = 'https://rankings.throwbackbuys.com/v1/carousel';

    /* ── Per-visit answer cache ───────────────────────────────────────────────
       The engine rotates picks in 15-minute bands, so within a browsing session
       the answer for a given row is stable. Caching it means page 2 onward never
       waits on the network to know what to show — which is what actually removes
       the visible switch. Measured 2026-08-27: the swap was landing ~3s in even
       after the viewport gate, because the module cannot run until the document
       finishes parsing (~2.3s) and only then starts asking.

       Keyed by the full request URL, so any change to limit/collection/client id
       is a different entry and there is no chance of serving a row the wrong set.
       sessionStorage, not local: it should not outlive the visit. */
    /* How long, since navigation, a VISIBLE row may have been on screen before
       swapping it stops being worth doing. Past this the shopper has already
       read the row and a change reads as a glitch; the answer is cached above,
       so the next page paints it instantly instead.

       4000ms, and the number was corrected by a test rather than chosen. At 2600ms
       the guard did the exact opposite of its job: measured live, it suppressed
       ROW ONE — the hero row, the only one guaranteed to be on screen — while
       rows two and three swapped freely, because those sit below the fold and are
       exempt. The visible row was the one losing its personalization, every load.

       The parse floor is why. A module cannot run before the document finishes
       parsing (~2.3s here), so a budget anywhere near 2.3s fires on the normal
       case instead of the pathological one. 4000ms leaves the normal path alone
       and still catches a genuinely stalled response. Lower it when page weight
       comes down, not before — and re-measure which rows swap when you do. */
    const LATE_SWAP_BUDGET_MS = 4000;
    const CACHE_TTL_MS = 15 * 60 * 1000;

    /* ── Rendered-row cache ───────────────────────────────────────────────────
       Caching the ANSWER removes the network wait but not the visible switch:
       the module that applies it still cannot run until the document finishes
       parsing (~2.3s here). Caching the RENDERED ROW lets the inline script in
       ai-carousel.liquid paint it DURING parse, so page 2 onward has no switch
       at all.

       TTL is 3 minutes, not 15. This HTML contains prices and sold-out badges,
       and a stale price on a live store is a real defect, not a cosmetic one.
       Three minutes bounds it, and an expiry simply produces one ordinary swap
       which re-caches.

       NEVER CACHED: any row containing <auction-info>. Auction cards carry live
       bid state and an inline script that feeds `auctionApp.updateAuctions()`.
       Scripts inserted via innerHTML do not execute, so a cached auction card
       would be frozen at an old bid with nothing to correct it. Those rows keep
       the normal path. */
    const HTML_TTL_MS = 3 * 60 * 1000;
    const HTML_MAX_BYTES = 200 * 1024;
    const htmlCacheSet = (url, html, ids) => {
        try {
            if (!url || !html || html.length > HTML_MAX_BYTES) return;
            if (/<auction-info/i.test(html)) return;   // live state — see above
            sessionStorage.setItem('tbb-carousel-html:' + url,
                JSON.stringify({ html: html, ids: ids, ts: Date.now() }));
        } catch (e) { /* quota or private mode: optimisation only */ }
    };
    const cacheGet = (url) => {
        try {
            const raw = sessionStorage.getItem('tbb-carousel:' + url);
            if (!raw) return null;
            const hit = JSON.parse(raw);
            if (!hit || !Array.isArray(hit.ids) || Date.now() - hit.ts > CACHE_TTL_MS) return null;
            return hit.ids;
        } catch (e) { return null; }
    };
    const cacheSet = (url, ids) => {
        try { sessionStorage.setItem('tbb-carousel:' + url, JSON.stringify({ ids: ids, ts: Date.now() })); }
        catch (e) { /* private mode or quota: caching is an optimisation, never required */ }
    };
    // Warm engine answers in ~2-3ms; the first cross-origin call per session pays ~600ms of
    // connection/cold-start, so the abort budget is generous. (Engine-side cold-start is Doc's.)
    // 1500, was 800. Engine measures /v1/carousel at 0.15-0.37s, so 800ms was not
    // generous - but credentials:"include" adds a CORS preflight on first load,
    // and the cohort row (the one with no Liquid fallback) was tipping over the
    // line and rendering EMPTY. Verified live 2026-08-26: five rows succeeded,
    // the cohort row logged `TBBFetch timed out after 800ms`.
    const ENGINE_TIMEOUT_MS = 1500;
    const BUCKET_COOKIE = 'tbb_ai_carousel_bucket';

    const readCookie = (name) => {
        const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
    };

    const pushDL = (payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
    };

    // Sticky session A/B bucket (no Expires/Max-Age = session cookie). 50/50.
    const resolveBucket = () => {
        let b = readCookie(BUCKET_COOKIE);
        if (b !== 'control' && b !== 'treatment') {
            b = Math.random() < 0.5 ? 'control' : 'treatment';
            document.cookie = BUCKET_COOKIE + '=' + b + '; path=/; SameSite=Lax';
        }
        return b;
    };

    // Opaque identifiers only.
    const getClientId = () => readCookie('_shopify_y');




    const KLAVIYO_RESOLVE_MS = 150;
    const looksLikeId = (v) =>
        typeof v === 'string' && v.length >= 6 && v.indexOf('@') === -1 && !/^\+?[\d\s-]+$/.test(v);

    // Klaviyo canonical profile_id (UUID/ULID-shaped) for identified shoppers; null for anonymous.
    const klaviyoSync = () => {
        try {
            const k = window.klaviyo;
            if (k && k.account) {
                const id = k.account.profile_id || k.account.$id;
                if (looksLikeId(id)) return id;
            }
            const lq = window._learnq;
            if (Array.isArray(lq)) {
                for (let i = lq.length - 1; i >= 0; i--) {
                    const e = lq[i];
                    if (Array.isArray(e) && (e[0] === 'identify' || e[0] === 'account') && e[1]) {
                        const p = e[1];
                        const id = p.$id || p.id || p.profile_id;
                        if (looksLikeId(id)) return id;
                    }
                }
            }
        } catch (e) {}
        return null;
    };
    const getKlaviyoId = async () => {
        const sync = klaviyoSync();
        if (sync) return sync;
        try {
            if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
                const profile = await Promise.race([
                    Promise.resolve(window.klaviyo.identify()),
                    new Promise((resolve) => setTimeout(() => resolve(null), KLAVIYO_RESOLVE_MS))
                ]);
                if (profile) {
                    const id = profile.$id || profile.id || profile.profile_id;
                    if (looksLikeId(id)) return id;
                }
            }
        } catch (e) {}
        return null;
    };

    const isDesignMode = () => !!(window.Shopify && window.Shopify.designMode);
    const shopRoot = () => {
        const r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
        return r ? r.replace(/\/$/, '') : '';
    };

    customElements.define('ai-carousel', class extends Core {
        elements = { content: '[data-ai-content]' };

        async render() {
            // ── VIEWPORT GATE ────────────────────────────────────────────────
            // Measured 2026-08-27 on the homepage: six carousels all ran this
            // pipeline at once on load — six /v1/carousel calls, six section
            // re-renders, and 72 product images forced eager and decoded
            // simultaneously, for five rows the shopper had not scrolled to.
            // 8.4MB / 504 requests, and the swap landed ~10s in, long after the
            // page looked finished. That is what made it read as broken.
            //
            // A row does none of this until the shopper is within 600px of it.
            // The first row still runs immediately (it is already intersecting),
            // so nothing above the fold gets slower.
            //
            // EXCEPTION: a row the theme renders display:none (the PDP `related`
            // and `recently_viewed` strategies set tbb_hide_style) has no layout
            // box and can therefore NEVER intersect. Gating those would hide them
            // forever. No box => run now, exactly as before.
            // Margin is 1.5 viewports, not a fixed pixel count. Too small and the
            // shopper scrolls into a row mid-swap — which is the exact ugliness
            // this is meant to remove, just moved down the page. 1.5 viewports
            // buys roughly a second of scrolling at normal speed, comfortably
            // more than the ~800ms the engine call plus section re-render takes,
            // while still leaving the bottom rows of a long homepage untouched
            // until they are genuinely approached.
            const box = this.getBoundingClientRect();
            const hasBox = box.width > 0 && box.height > 0;
            if (hasBox && typeof IntersectionObserver === 'function') {
                const margin = Math.max(600, Math.round((window.innerHeight || 800) * 1.5));
                await new Promise((resolve) => {
                    const io = new IntersectionObserver((entries) => {
                        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); resolve(); }
                    }, { rootMargin: margin + 'px 0px' });
                    io.observe(this);
                });
            }
            return this._render();
        }

        async _render() {
            const surfaceId = this.getAttribute('data-surface') || 'home-hero';
            const limit = parseInt(this.getAttribute('data-limit'), 10) || 12;
            const engineEnabled = this.getAttribute('data-engine-enabled') === 'true';
            const strategyFallback = this.getAttribute('data-strategy-fallback') || 'cohort_affinity_v1';
            // Optional collection-eligibility mode: when set, the engine ranks only within this
            // Shopify collection handle (?collection=) instead of the sitewide trending blob;
            // vendorCap ('1') asks the engine to cap to one product per seller (?vendor_cap=).
            // Empty = legacy sitewide behavior, unchanged.
            const collection = this.getAttribute('data-collection') || '';
            const vendorCap = this.getAttribute('data-vendor-cap') || '';
            // vendorRandomize ('1') spreads sellers so one batch upload cannot own
            // the row. Distinct from vendorCap, which is a hard one-each limit.
            const vendorRandomize = this.getAttribute('data-vendor-randomize') || '';
            // Blank for logged-out shoppers, in which case the param is omitted
            // entirely and nothing about their request changes.

            // ── PDP engine strategies (additive; every legacy carousel omits these
            //    attributes and therefore keeps its exact previous behavior) ──
            //   data-mode="recently_viewed" -> ?recently_viewed=1
            //   data-mode="related"         -> ?related_to=<gid>
            //   data-exclude=<gid>          -> ?exclude=<gid>   (current PDP product)
            //   data-on-empty="hide"        -> remove the section instead of keeping a
            //     server-rendered fallback row. REQUIRED for Boost displacement: leaving
            //     a fallback visible means Boost was never actually replaced.
            //   data-section-id             -> which section answers the hydration call
            const mode = this.getAttribute('data-mode') || '';
            const relatedTo = this.getAttribute('data-related-to') || '';
            const excludeId = this.getAttribute('data-exclude') || '';
            const hideOnEmpty = this.getAttribute('data-on-empty') === 'hide';
            const hydrateSection = this.getAttribute('data-section-id') || 'ai-carousel';

            // A/B holdout toggle: ON = 50/50 split; OFF (default) = everyone is treatment.
            const abHoldout = this.getAttribute('data-ab-holdout') !== 'false';
            const bucket = abHoldout ? resolveBucket() : 'treatment';
            const clientId = getClientId();
            const klaviyoId = await getKlaviyoId();
            window.tbb_engine = Object.assign({}, window.tbb_engine, { klaviyo_id: klaviyoId, client_id: clientId });

            pushDL({ event: 'ai_carousel_bucket', surface_id: surfaceId, bucket: bucket });
            this._bindObservability(surfaceId, bucket, strategyFallback);

            // The fallback products are already rendered + visible, with the native CSS loader
            // covering them until images paint. Only the treatment bucket with the engine on
            // swaps in personalized picks; everyone else just keeps the fallback (like any
            // other carousel). Nothing to hide, nothing to reveal.
            if (isDesignMode() || !engineEnabled || bucket !== 'treatment') {
                pushDL({
                    event: 'ai_carousel_fetch',
                    status: 'fallback',
                    surface_id: surfaceId,
                    bucket: bucket,
                    strategy_id: strategyFallback,
                    reason: isDesignMode() ? 'design_mode' : (!engineEnabled ? 'engine_disabled' : 'control_bucket')
                });
                // Control bucket rendering nothing IS the control arm of the displacement
                // test. Design mode is the exception -- keep it on screen or the merchant
                // cannot select or position the section in the theme editor.
                if (hideOnEmpty && !isDesignMode()) this._hideSection();
                return;
            }

            pushDL({ event: 'ai_carousel_fetch', status: 'start', surface_id: surfaceId, bucket: bucket, strategy_id: strategyFallback, collection: collection || undefined });
            this._startedAt = performance.now();

            try {
                const data = await this._fetchEngine(surfaceId, limit, clientId, klaviyoId, collection, vendorCap, mode, relatedTo, excludeId, vendorRandomize);
                const ids = (data && Array.isArray(data.products) ? data.products : [])
                    .map((gid) => String(gid).split('/').pop())
                    .filter(Boolean);

                if (!ids.length || data.fallback === true) {
                    pushDL({
                        event: 'ai_carousel_fetch',
                        status: 'fallback',
                        surface_id: surfaceId,
                        bucket: bucket,
                        strategy_id: (data && data.strategy_id) || strategyFallback,
                        cohort: data && data.cohort,
                        reason: (data && data.reason) || 'engine_empty_or_fallback',
                        personalized: data && data.personalized
                    });
                    if (hideOnEmpty) this._hideSection();
                    return; // legacy carousels keep their native fallback products
                }

                /* ── NEVER LATE-SWAP ──────────────────────────────────────────
                   A swap the shopper watches happen is worse than no swap at
                   all. If this row has been sitting visible on screen past the
                   budget, keep what is there and let the answer — already
                   cached above — paint instantly on the next page instead.
                   Rows that are off screen, hidden, or served from cache are
                   exempt: there is nothing for anyone to see. */
                /* The inline script already painted this row from cache during
                   parse. Re-swapping now would produce the exact flicker the
                   cache exists to prevent, so this page shows what it has and
                   the id cache (updated in _fetchEngine) carries any change to
                   the next page. The 3-minute HTML TTL bounds how long that can
                   persist — on expiry the next load does one ordinary swap and
                   re-caches. */
                if (this.getAttribute('data-tbb-prehydrated') === '1') {
                    pushDL({
                        event: 'ai_carousel_fetch', status: 'prehydrated', surface_id: surfaceId,
                        bucket: bucket, strategy_id: data.strategy_id || strategyFallback
                    });
                    if (hideOnEmpty) this._revealSection();
                    return;
                }

                const rect = this.getBoundingClientRect();
                const visibleNow = rect.height > 0 && rect.bottom > 0 && rect.top < (window.innerHeight || 0);
                /* Measured from NAVIGATION, not from when the fetch started. The
                   first version of this used fetch-start and was meaningless: the
                   module cannot run until the document finishes parsing, so the
                   fetch always looked fast (~300ms) even when the shopper had
                   been staring at the row for three seconds. What matters is how
                   long the WRONG products have been on screen. */
                /* No cache exemption. A cached answer painted at 2.4s looks
                   exactly as jarring as a fresh one — the shopper cannot tell
                   where the products came from. Exempting it would have made
                   page 1 and page 2 behave differently for no reason the shopper
                   could perceive. Same rule for both. */
                const tooLate = visibleNow && performance.now() > LATE_SWAP_BUDGET_MS;
                if (tooLate) {
                    pushDL({
                        event: 'ai_carousel_fetch', status: 'deferred', surface_id: surfaceId,
                        bucket: bucket, strategy_id: data.strategy_id || strategyFallback,
                        reason: 'late_swap_suppressed'
                    });
                    if (hideOnEmpty) { await this._swap(ids, hydrateSection); this._revealSection(); }
                    return;
                }

                await this._swap(ids, hydrateSection);
                if (hideOnEmpty) this._revealSection();

                pushDL({
                    event: 'ai_carousel_fetch',
                    status: 'success',
                    surface_id: surfaceId,
                    bucket: bucket,
                    strategy_id: data.strategy_id || strategyFallback,
                    cohort: data.cohort,
                    collection: data.collection || collection || undefined,
                    blob_computed_at: data.blob_computed_at,
                    personalized: data.personalized,
                    count: ids.length
                });
            } catch (err) {
                pushDL({
                    event: 'ai_carousel_fetch',
                    status: 'fallback',
                    surface_id: surfaceId,
                    bucket: bucket,
                    strategy_id: strategyFallback,
                    // TBBFetch rejects with a tagged Error, not an AbortError, so the
                    // old name check alone would have logged every timeout as 'error'.
                    reason: (err && (err.tbbReason === 'timeout' || err.name === 'AbortError'))
                        ? 'timeout'
                        : ((err && err.tbbReason) || 'error'),
                    // `reason:"error"` alone is undiagnosable from analytics - it hid a
                    // ReferenceError on all six carousels on 2026-08-26. Carry the real
                    // message and name so the next failure is readable without a source dive.
                    error_message: (err && err.message) ? String(err.message).slice(0, 200) : undefined,
                    error_name: (err && err.name) || undefined
                });
                if (hideOnEmpty) this._hideSection();
                // legacy carousels keep their fallback products visible — never white-screen
            }
        }

        async _fetchEngine(surfaceId, limit, clientId, klaviyoId, collection, vendorCap, mode, relatedTo, excludeId, vendorRandomize) {
            const params = new URLSearchParams({ limit: String(limit) });
            // SURFACE IS OMITTED ON PURPOSE for the PDP strategies. They rerank the
            // home-hero cohort blob using current-product attrs + visitor affinity;
            // passing surface=pdp-* hits an empty blob and returns fallback:true with
            // zero products, so the row hides and looks broken. Engine instruction,
            // verified live 2026-08-12 (worker 42eab62d): omit it entirely.
            if (!mode) params.set('surface', surfaceId);
            // recently_viewed is device-scoped, so klaviyo_id adds nothing there.
            if (mode === 'recently_viewed') params.set('recently_viewed', '1');
            if (mode === 'related' && relatedTo) params.set('related_to', relatedTo);
            if (excludeId) params.set('exclude', excludeId);
            /* SECURITY 2026-08-26 (engine): `customer_id` and `klaviyo_id` are NO
               LONGER SENT. As plain URL params they let anyone graft onto a
               victim's profile - customer ids are sequential and trivial to
               enumerate. The engine now ignores both on this endpoint, and
               cross-device linking moved to the signed app-proxy identity call
               below, where Shopify supplies and HMAC-signs the customer id and
               our code never touches it.

               `client_id` STAYS: it is the browser's own `_shopify_y` cookie,
               not an account identifier, and it remains the cache key. Do not
               re-add the other two. */
            if (clientId) params.set('client_id', clientId);
            // Collection-eligibility mode + per-seller diversity cap (both optional).
            // `collection` is the canonical key and takes a real collection handle.
            // `handle` is NOT a parameter the engine reads - it is ignored and the
            // request falls through to the sitewide cohort carousel. Do not add it.
            if (collection) params.set('collection', collection);
            if (vendorCap) params.set('vendor_cap', vendorCap);
            if (vendorRandomize) params.set('vendor_randomize', vendorRandomize);

            // Deadline via TBBFetch: raced against its own timer, so it still fires
            // even if whatever owns window.fetch on the storefront drops the abort
            // signal. See DEV_NOTES 2026-07-29. credentials stays explicit here.
            //
            // `include`, NOT `omit` (engine ask, 2026-08-26). The FPID cookie is set
            // server-side by sGTM with a 2-year max-age and is HttpOnly, so theme JS
            // can neither read it nor forward it as a param - it only reaches the
            // engine if the request carries credentials. `client_id` (from
            // `_shopify_y`) is still sent in the query string and remains the cache
            // key and primary identifier; FPID only adds durability on top.
            //
            // Why this matters: `_shopify_y` is JS-written, so browsers cap it at 7
            // days. Measured across August, only 1.6% of visitors were recognised on
            // a second day and 0.8% ever reached the 10 events the affinity model
            // needs. FPID is not subject to that cap.
            //
            // Safe with credentials, verified independently against prod 2026-08-26:
            // /v1/carousel echoes `access-control-allow-origin: https://throwbackbuys.com`
            // (a SPECIFIC origin, not `*` - `*` plus credentials is blocked outright
            // by the browser and would have killed every carousel), sends
            // `access-control-allow-credentials: true` on both the GET and the OPTIONS
            // preflight, and sets `vary: Origin`. Personalized responses are
            // `Cache-Control: private, max-age=5`, so there is no cross-visitor cache
            // leak.
            const url = ENGINE_ENDPOINT + '?' + params.toString();
            this._engineUrl = url;

            /* 1. Already answered this visit? Use it. No network, no wait. */
            const cached = cacheGet(url);
            if (cached && cached.length) {
                this._fromCache = true;
                return { products: cached, fallback: false, strategy_id: 'cached_v1' };
            }

            /* 2. Did the inline prefetch in ai-carousel.liquid already start this
                  exact request during parse? Adopt it rather than firing a second.
                  The URL must match byte for byte or we ignore it — a mismatched
                  prefetch is a wasted request, never a wrong answer. */
            const pre = window.__tbbEnginePrefetch && window.__tbbEnginePrefetch[url];
            if (pre) {
                delete window.__tbbEnginePrefetch[url];
                try {
                    const data = await pre;
                    if (data && Array.isArray(data.products)) {
                        this._prefetched = true;
                        if (data.fallback !== true) cacheSet(url, data.products);
                        return data;
                    }
                } catch (e) { /* prefetch failed; fall through to a normal fetch */ }
            }

            const res = await (window.TBBFetch || fetch)(url, {
                timeout: ENGINE_TIMEOUT_MS,
                headers: { Accept: 'application/json' },
                credentials: 'include'
            });
            if (!res.ok) throw new Error('engine_http_' + res.status);
            const data = await res.json();
            if (data && Array.isArray(data.products) && data.fallback !== true) cacheSet(url, data.products);
            return data;
        }

        async _swap(ids, hydrateSection) {
            // Single Section Rendering call. Shopify storefront search needs explicit OR between
            // id: clauses (comma / whitespace return 0 results — confirmed by merchandising).
            const q = ids.map((id) => 'id:' + id).join(' OR ');
            const url = shopRoot() + '/search?section_id=' + encodeURIComponent(hydrateSection || 'ai-carousel') +
                '&type=product&q=' + encodeURIComponent(q);
            const doc = await fetchHTML(url);
            const fresh = doc.querySelector('[data-ai-content]');
            if (!fresh || !this.$content) return;
            this._reorder(fresh, ids);

            // DOUBLE-BUFFER swap. Replacing [data-ai-content] live makes the shopper
            // watch the fallback->engine cards render in (faint titles, frames
            // settling, the flash). Instead we stage the new cards OFFSCREEN in the
            // live document so their images actually fetch + decode, then drop them
            // in already-finished — one atomic swap, no visible transition.
            const buffer = document.createElement('div');
            buffer.setAttribute('aria-hidden', 'true');
            buffer.style.cssText = 'position:absolute;left:-99999px;top:0;pointer-events:none;width:' +
                ((this.$content && this.$content.offsetWidth) || window.innerWidth) + 'px;';
            buffer.appendChild(fresh);
            document.body.appendChild(buffer);

            // Product-card images are lazy + offscreen here, so they wouldn't fetch
            // on their own — force them eager, then wait for them to decode. Capped
            // at 1.5s so a slow/broken image can never hang the swap.
            //
            // ONLY when the row is actually on screen. Pre-decoding exists to hide
            // a visible transition; for a row still below the fold there is no
            // transition to hide, so this would be 24 image downloads bought for
            // nothing. Six rows doing it unconditionally is how the homepage ended
            // up with 72 simultaneous eager image loads. The viewport gate above
            // means an off-screen row now normally swaps well before it is
            // reached, with its images left lazy and fetched on approach.
            const r = this.getBoundingClientRect();
            const onScreen = r.bottom > 0 && r.top < (window.innerHeight || 0);
            if (onScreen) {
                const imgs = Array.from(fresh.querySelectorAll('img'));
                imgs.forEach((img) => { img.loading = 'eager'; });
                const decoded = Promise.all(imgs.map((img) =>
                    (img.decode ? img.decode() : Promise.resolve()).catch(() => {})
                ));
                await Promise.race([decoded, new Promise((resolve) => setTimeout(resolve, 1500))]);
            }

            // Atomic swap (guard against the row being gone by now).
            if (!this.$content || !this.$content.parentNode) { buffer.remove(); return; }
            this.$content.replaceWith(fresh);
            buffer.remove();

            // Re-wire the native arrows + start the fresh scroller at the first card.
            if (window.tbbWireNativeCarousels) window.tbbWireNativeCarousels();
            const newTrack = fresh.querySelector('.tbb-ns-track');
            if (newTrack) newTrack.scrollLeft = 0;

            /* Bank the rendered row so the inline script can paint it during parse
               on the next page of this visit — the only way to land it before the
               module can run. htmlCacheSet refuses auction rows and oversized
               payloads; see its comment. */
            this._cacheRendered(fresh, ids);
        }

        // Hide/reveal the whole Shopify section wrapper, not just this element, so no
        // empty shell, heading or shell border is left behind on the PDP.
        _sectionRoot() {
            return this.closest('.shopify-section') || this;
        }
        // The PDP snippet renders the SHELL with an inline display:none (so the row
        // never flashes empty before the engine answers), while hide-on-empty wants
        // the whole SECTION gone so no stray border or heading is left behind.
        // Those are two different elements: reveal MUST clear both, or a successful
        // swap still renders into a permanently hidden shell. That was the bug on
        // 2026-08-13 -- engine returned 8 products, status=success, nothing visible.
        _shellRoot() {
            return this.closest('.tbb-carousel-shell');
        }
        _cacheRendered(fresh, ids) {
            try {
                if (!this._engineUrl || !fresh) return;
                htmlCacheSet(this._engineUrl, fresh.innerHTML, ids);
            } catch (e) { /* never let caching break a successful swap */ }
        }

        _hideSection() {
            const root = this._sectionRoot();
            if (root) root.style.display = 'none';
        }
        _revealSection() {
            const root = this._sectionRoot();
            if (root) root.style.removeProperty('display');
            const shell = this._shellRoot();
            if (shell) shell.style.removeProperty('display');
        }

        // Shopify search does not guarantee id: order; restore the engine's ranking.
        _reorder(container, ids) {
            try {
                const wrap = container.querySelector('.tbb-ns-track') || container;
                const slides = Array.from(wrap.querySelectorAll('.tbb-ns-slide'));
                if (!slides.length) return;
                const order = new Map(ids.map((id, i) => [String(id), i]));
                slides
                    .filter((s) => s.getAttribute('data-product-id'))
                    .sort((a, b) => {
                        const ai = order.has(a.getAttribute('data-product-id')) ? order.get(a.getAttribute('data-product-id')) : 999;
                        const bi = order.has(b.getAttribute('data-product-id')) ? order.get(b.getAttribute('data-product-id')) : 999;
                        return ai - bi;
                    })
                    .forEach((s) => wrap.appendChild(s));
            } catch (e) {
                /* leave order as-is */
            }
        }

        _bindObservability(surfaceId, bucket, strategyId) {
            this.addEventListener('click', (e) => {
                const link = e.target.closest('[data-ai-content] a[href*="/products/"]');
                if (link) {
                    pushDL({
                        event: 'ai_carousel_click',
                        surface_id: surfaceId,
                        bucket: bucket,
                        strategy_id: strategyId,
                        product_url: link.getAttribute('href')
                    });
                    return;
                }
                const atc = e.target.closest('[data-ai-content] [name="add"], [data-ai-content] button[type="submit"]');
                if (atc && atc.closest('form[action*="/cart"]')) {
                    pushDL({
                        event: 'ai_carousel_add_to_cart',
                        surface_id: surfaceId,
                        bucket: bucket,
                        strategy_id: strategyId
                    });
                }
            }, true);
        }
    });
})();
